import { ActionTree } from 'vuex';
import router from '../../../router/index';
import ApiUrl from '../../../api/url';
import { UserGroupState, NewUserGroup, EditUserGroup, SelectedUsers } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<UserGroupState, RootState> = {
    /**
     * ユーザーグループ一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} ignoreUserGroupIds 除外するユーザーグループID
     * @param {any} provider 検索対象の認証プロバイダ
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search(
        { dispatch, commit },
        { ignoreUserGroupIds = undefined, provider = '', pageNumber, perPage, sort } = {}
    ) {
        const payload = {
            ignore_user_group_ids: ignoreUserGroupIds,
            provider: provider,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.UserGroup.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザーグループ新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_USER_GROUP);
    },

    /**
     * ユーザーグループ新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewUserGroup} newUserGroup 新規ユーザーグループ情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newUserGroup: NewUserGroup) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.UserGroup.CREATE, params: newUserGroup },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserGroup.CREATED);
                dispatch('toastMessage', `${newUserGroup.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ユーザーグループの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ユーザーグループID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.UserGroup.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザーグループ更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditUserGroup} editUserGroup 更新するユーザーグループ情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editUserGroup: EditUserGroup) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.UserGroup.UPDATE.replace(':id', editUserGroup.id.toString()),
                    params: editUserGroup,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserGroup.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ユーザーグループ削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ユーザーグループID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.UserGroup.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserGroup.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 所属ユーザー追加前のエラー等のクリア
     * 前回にユーザー追加に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async newUsers({ commit }) {
        commit(types.NEW_USERS);
    },

    /**
     * 所属ユーザー追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedUsers} newUsers 追加する所属ユーザー情報
     * @returns {Promise<void>}
     */
    async addUsers({ dispatch, commit, rootState }, newUsers: SelectedUsers) {
        try {
            commit(types.ADD_USERS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.UserGroup.ADD_USERS.replace(':id', newUsers.id.toString()),
                    params: newUsers,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_USERS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserGroup.ADD_USERS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_USERS_FAILURE, params);
        }
    },

    /**
     * 所属ユーザー除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedUsers} removeUsers 除外するユーザー情報
     * @returns {Promise<void>}
     */
    async removeUsers({ dispatch, commit, rootState }, removeUsers: SelectedUsers) {
        try {
            commit(types.REMOVE_USERS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.UserGroup.REMOVE_USERS.replace(':id', removeUsers.id.toString()),
                    params: removeUsers,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_USERS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserGroup.REMOVE_USERS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_USERS_FAILURE, params);
        }
    },

    /**
     * ユーザグループ全件取得
     * そのまま全件取得できないため、1ページあたりの件数を多く指定
     * @returns {Promise<void>}
     */
    async fetchAll({ dispatch, commit }) {
        const payload = {
            page_number: 1,
            per_page: 1000000,
            'sort[column]': '',
            'sort[direction]': '',
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.UserGroup.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { sort } = {}) {
        const payload = {
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.UserGroup.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
